// A lot of the amplify commands use underscores...
/* eslint-disable @typescript-eslint/camelcase */

import { handleActions } from 'redux-actions';
import { Auth } from 'aws-amplify';
import Cache from '@aws-amplify/cache';
import moment from 'moment-timezone';
import { AnyAction } from 'redux';
import jwt from 'jsonwebtoken';
import Honeybadger from '@honeybadger-io/js';

import BugTracker from 'kb-shared/utilities/bugTracker';
import { sanitizeUsername } from 'kb-shared/utilities/auth.helper';
import client from 'kb-shared/graphql/client';
import {
  CREATE_PATIENT,
  REGISTER_PATIENT_TO_EVENT,
  UPDATE_PATIENT_EMAIL_CONFIRMED
} from 'kb-shared/graphql/mutations';
import { PATIENT } from 'kb-shared/graphql/queries';
import {
  getIdToken,
  isEpicUser,
  isMedtronicUser,
  logout as apiLogout
} from 'kb-shared/utilities/api';
import ssoConfig from 'kb-shared/config/sso';
import { clearPwLocalStorage } from 'kb-shared/utilities/clear_pw_storage';
import loadGoogleAPI, { signGoogleUserOut } from 'kb-shared/utilities/auth.google';
import { updateFederatedId, doesUsernameExists } from 'kb-shared/utilities/amplify.user';
import * as actions from './actions';
import {
  waitUntilEpicSsoIdTokenIsSet,
  getEpicSsoIdToken,
  waitUntilMedtronicSsoIdTokenIsSet,
  getMedtronicSsoIdToken,
  getAppleSsoIdToken,
  getGoogleIdToken
} from 'kb-shared/utilities/api';

import {
  Patient,
  LoginError,
  ReduxState,
  SignUpError,
  GoogleAuthError,
  ConfirmationError,
  ProfileImageError,
  InsuranceCardError,
  InsuranceCardSide,
  PatientState,
  SignUpProps,
  IDSide,
  IDError
} from 'kb-shared/types';
import { AppleAuthError } from 'kb-shared';

/* ---------- ACTIONS  ---------- */
const APP_LOADED = 'kindbody/user/APP_LOADED';
const REFRESH_USER = 'kindbody/user/REFRESH_USER';
const REFRESH_USER_SUCCESS = 'kindbody/user/REFRESH_USER_SUCCESS';
const REFRESH_USER_FAILED = 'kindbody/user/REFRESH_USER_FAILED';
const SIGNUP = 'kindbody/user/SIGNUP';
const CLEAR_GOOGLE_AUTH_ERROR = 'kindbody/user/CLEAR_GOOGLE_AUTH_ERROR';
const SIGNUP_GOOGLE_USER = 'kindbody/user/SIGNUP_GOOGLE_USER';
const SIGNUP_SUCCESS = 'kindbody/user/SIGNUP_SUCCESS';
const SIGNUP_FAILED = 'kindbody/user/SIGNUP_FAILED';
const CONFIRM_USER = 'kindbody/user/CONFIRM_USER';
const CONFIRM_USER_SUCCESS = 'kindbody/user/CONFIRM_USER_SUCCESS';
const CONFIRM_USER_FAILED = 'kindbody/user/CONFIRM_USER_FAILED';
const RESEND_CONFIRMATION_CODE = 'kindbody/user/RESEND_CONFIRMATION_CODE';
const RESEND_CONFIRMATION_CODE_SUCCESS = 'kindbody/user/RESEND_CONFIRMATION_CODE_SUCCESS';
const RESEND_CONFIRMATION_CODE_FAILURE = 'kindbody/user/RESEND_CONFIRMATION_CODE_FAILURE';
const AUTH_WITH_GOOGLE = 'kindbody/user/AUTH_WITH_GOOGLE';
const AUTH_WITH_GOOGLE_FAILED = 'kindbody/user/AUTH_WITH_GOOGLE_FAILED';
const SIGNIN_APPLE_USER = 'kindbody/user/SIGNIN_APPLE_USER';
const SET_APPLE_AUTH_ERROR = 'kindbody/user/APPLE_AUTH_ERROR';
const LOGIN = 'kindbody/user/LOGIN';
const LOGIN_FAILED = 'kindbody/user/LOGIN_FAILED';
const LOGIN_SUCCESS = 'kindbody/user/LOGIN_SUCCESS';
const LOGIN_ERROR_HANDLED = 'kindbody/user/LOGIN_ERROR_HANDLED';
export const LOG_OUT = 'kindbody/user/LOGOUT';
const UPDATE_PROFILE_IMAGE = 'kindbody/user/UPDATE_PROFILE_IMAGE';
const PROFILE_IMAGE_ERROR = 'kindbody/user/PROFILE_IMAGE_ERROR';
const RESET_PROFILE_IMAGE_ERROR = 'kindbody/user/RESET_PROFILE_IMAGE_ERROR';
const UPDATE_SUBMITTED_INSURANCE_CARD = 'kindbody/user/UPDATE_SUBMITTED_INSURANCE_CARD';
const INSURANCE_CARD_ERROR = 'kindbody/user/INSURANCE_CARD_ERROR';
const UPDATE_USER_NAME = 'kindbody/user/UPDATE_NAME';
const RESET_INSURANCE_CARD_ERROR = 'kindbody/user/RESET_INSURANCE_CARD_ERROR';
const RESET_ID_ERROR = 'kindbody/user/RESET_ID_ERROR';
const UPDATE_SUBMITTED_ID = 'kindbody/user/UPDATE_SUBMITTED_ID';
const ID_ERROR = 'kindbody/user/ID_ERROR';
/* ---------- LOGOUT ---------- */

function _logout(loginUrl?: string) {
  return {
    type: LOG_OUT,
    loginUrl
  };
}
export function logout() {
  return async (dispatch: Function) => {
    try {
      let redirectUrl = undefined;
      if (isEpicUser()) redirectUrl = ssoConfig.epicDisney.logoutUrl;
      if (isMedtronicUser()) redirectUrl = ssoConfig.azureMedtronic.logoutUrl;

      await apiLogout(); // sets cognitoUserId to null & resets apollo store
      await Auth.signOut();
      await signGoogleUserOut();
      dispatch(_logout(redirectUrl));
    } catch (error) {
      BugTracker.notify(error, 'LogoutError');
    }
  };
}

/* ---------- REFRESH USER ---------- */

function refreshPatientSuccess(patient: Patient) {
  return {
    type: REFRESH_USER_SUCCESS,
    patient
  };
}

function refreshPatientFailed() {
  return {
    type: REFRESH_USER_FAILED
  };
}

function _refreshPatient() {
  return {
    type: REFRESH_USER
  };
}

async function confirmPatientInOurDatabase() {
  try {
    await client.mutate({
      mutation: UPDATE_PATIENT_EMAIL_CONFIRMED,
      variables: {
        confirmed: true
      }
    });
  } catch (error) {
    BugTracker.notify(error, 'ConfirmUserMutationError');
  }
}

function identifyPatient(patient: Patient) {
  if (patient && patient.id && window && window.analytics) {
    window.analytics.identify(patient.id, {
      email: patient.email,
      firstname: patient.firstName,
      lastname: patient.lastName,
      phone: patient.phone,
      lab_id: patient.lab?.id,
      account_creation_date: (patient as any).createdAt,
      employer: patient.patientMembership?.employerName,
      employer_membership: patient.patientMembership?.membership?.name
    });
    window.analytics.page({
      context: {
        traits: {}
      }
    });
  }
}

function hasSsoToken() {
  const isEpicSso = getEpicSsoIdToken();
  const isMedtronicSso = getMedtronicSsoIdToken();
  const isAppleSso = getAppleSsoIdToken();
  const isGoogleSso = getGoogleIdToken();

  return isEpicSso || isMedtronicSso || isAppleSso || isGoogleSso;
}

export function refreshPatient() {
  return async (dispatch: Function) => {
    dispatch(_refreshPatient());

    try {
      let {
        data: { patient }
      } = await client.query({
        query: PATIENT
      });

      if (!hasSsoToken()) {
        await updateFederatedId(patient);

        const isConfirmed = patient.confirmed;
        const cognitoUser = await Auth.currentAuthenticatedUser();
        const cognitoConfirmed =
          cognitoUser.signInUserSession &&
          cognitoUser.signInUserSession.idToken.payload.email_verified;

        if (cognitoConfirmed && !isConfirmed) {
          await confirmPatientInOurDatabase();

          const { data } = await client.query({ query: PATIENT, fetchPolicy: 'no-cache' });

          patient = data.patient;
        }
      }

      identifyPatient(patient);

      dispatch(refreshPatientSuccess(patient));
    } catch (error) {
      Honeybadger.notify(error);
      // OK if this fails, as user might not be logged in
      dispatch(refreshPatientFailed());
      dispatch(logout());
    }
  };
}

function registerPatientToEvent(event: string) {
  return () =>
    client.mutate({
      mutation: REGISTER_PATIENT_TO_EVENT,
      variables: { event }
    });
}

/* ---------- LOGIN ---------- */

function loginFailed(error: LoginError) {
  return {
    type: LOGIN_FAILED,
    error
  };
}

function _loginSuccess(patient?: Patient | null) {
  return {
    type: LOGIN_SUCCESS,
    patient
  };
}

function _getEventParam() {
  const search = window?.location?.search;
  if (!search) return null;
  const urlParams = new URLSearchParams(search);
  return urlParams.get('event');
}

function loginSuccess(patient?: Patient | null) {
  return async (dispatch: Function, getState: () => ReduxState) => {
    // We need to get the parameter before _loginSuccess otherwise we lose them
    // when redirecting to the dashboard
    const event = _getEventParam();
    dispatch(_loginSuccess(patient));

    if (!hasSsoToken()) {
      // Verify that user is marked as confirmed in our databse
      const { patient: patientState } = getState();
      const isConfirmed = patientState.patient.confirmed;
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const cognitoConfirmed =
        cognitoUser.signInUserSession &&
        cognitoUser.signInUserSession.idToken.payload.email_verified;

      if (cognitoConfirmed && !isConfirmed && patientState.patient.id) {
        confirmPatientInOurDatabase();
      }
    }
    dispatch(refreshPatient());
    if (event) {
      dispatch(registerPatientToEvent(event));
    }

    if (window.analytics) {
      window.analytics.track('Patient Logged In');
    }
  };
}

function _login(username: string, password: string) {
  return {
    type: LOGIN,
    username,
    password
  };
}

export function login(username: string, password: string, noLogout = false) {
  return async (dispatch: Function) => {
    if (!noLogout) await dispatch(logout());
    // clear any old user info
    const cleanUserName = sanitizeUsername(username);
    await dispatch(_login(cleanUserName, password));

    try {
      // Attempt sign in
      await Auth.signIn(cleanUserName, password);
      // Get current user to check status.
      // This will throw an error if user is unconfirmed
      await Auth.currentAuthenticatedUser();
      await dispatch(loginSuccess());
    } catch (error) {
      if (error.code === 'UserNotConfirmedException') {
        await dispatch(loginFailed({ type: 'Unconfirmed' }));
      } else if (
        error.code == 'NotAuthorizedException' &&
        error.message == 'Password attempts exceeded'
      ) {
        await dispatch(loginFailed({ type: 'LimitExceededException' }));
      } else if (
        error.code === 'NotAuthorizedException' ||
        error.code === 'UserNotFoundException'
      ) {
        await dispatch(loginFailed({ type: 'InvalidCredentials' }));
      } else {
        BugTracker.notify(error, 'LoginError');
        await dispatch(loginFailed({ type: 'Unknown' }));
      }
    }
  };
}

/*
  Used in the mobile app to clear the login error
  when it's handled.
*/
export function loginErrorHandled() {
  return {
    type: LOGIN_ERROR_HANDLED
  };
}

/* ---------- GOOGLE AUTH ---------- */

function _googleAuthError(error: GoogleAuthError) {
  return {
    type: AUTH_WITH_GOOGLE_FAILED,
    error
  };
}

function _authWithGoogle(
  id: string,
  token: string,
  email: string,
  firstName: string,
  lastName: string
) {
  return {
    type: AUTH_WITH_GOOGLE,
    id,
    token,
    email,
    firstName,
    lastName
  };
}

export function authWithGoogle(
  id: string,
  token: string,
  expiry: number,
  email: string,
  firstName: string,
  lastName: string
) {
  return async (dispatch: Function) => {
    await dispatch(logout()); // clear any old user info
    await dispatch(_authWithGoogle(id, token, email, firstName, lastName));

    try {
      // Before calling federated sign in, check to see if a user has
      // already created an account via _email sign up_ in Cognito.
      // We will use this later when catching errors
      // Note that this does not catch if the user has already signed in via Google/Federated
      const usernameExists = await doesUsernameExists(email);

      if (token) await Cache.setItem('@GoogleSSO:idToken', token);

      try {
        // Attempt to query for the current patient's user data.
        // If this succeeds, it means the user is properly logged in
        // via Amplify AND that they already have a 'patient' record created in our DB
        // If it fails, it is most likely that we have not created a user record for them
        // in our DB since we previously auth'd them above via federatedSignIn
        const { data } = await client.query({
          query: PATIENT
        });
        const { patient } = data;

        if (patient && patient.id) {
          // If so update redux to mark user as Logged in
          await dispatch(loginSuccess());
        }
      } catch (error) {
        Honeybadger.notify(error);
        // If querying for the patient failed, and if the user already exists in amplify as a
        // user that has signed up *via email*, then
        if (usernameExists) {
          // In cases where the user already exists as an Email Address, we are in a bit of trouble.
          // This indicates that the user has previously signed up with *their email address* (NOT using Google Sign In!)
          // And they now have an Amplify Cognito user for their email, as well as a Amplify Federated ID for their Google Sign In.
          // In this case, we direct the user to sign in using their Email Account.
          await dispatch(_googleAuthError({ type: 'UserAlreadyExistsAsEmail' }));
          await dispatch(logout());
          return;
        }

        // If querying for the patient fails, no record exists in the db yet.
        // If an email account userName does not exist (see the above check)
        // then that, combined w/ no patient in the DB, means the google user needs to
        // finish signing up, which creates a patietn in our DB
        await dispatch(_googleAuthError({ type: 'UserHasNotSignedUp' }));
      }
    } catch (error) {
      await dispatch(logout());
      BugTracker.notify(error, 'GoogleAuthError');
    }
  };
}

/**
 * This is called "automatically" via the listener whenever the user's
 * Google auth status changs. The listening is initialized in auth.google.ts
 * via the below function `loadGoogleAuth`
 */
async function handleGoogleAuthChange(token: string, dispatch: Function, state: ReduxState) {
  const {
    patient: { userRefreshFailed }
  } = state;

  await dispatch(actions.googleAuthInitialized());

  if (userRefreshFailed) {
    await dispatch(logout());
  }

  if (!token) return;

  try {
    // Get the Google user's details
    const { aud, email, given_name, family_name, sub, exp } = jwt.decode(token);

    await dispatch(authWithGoogle(sub, token, exp, email, given_name, family_name));
  } catch (error) {
    BugTracker.notify(error, 'GoogleAuthDetailsError');
  }
}

export function loadGoogleAuth(jwt: string) {
  return (dispatch: Function, getState: () => ReduxState) => {
    handleGoogleAuthChange(jwt, dispatch, getState());
  };
}

export function clearGoogleAuthError() {
  return {
    type: CLEAR_GOOGLE_AUTH_ERROR
  };
}

/* ---------- INITIAL APP LOAD ---------- */

function _appLoaded() {
  return {
    type: APP_LOADED
  };
}

export function appLoaded(platform: 'web' | 'native' = 'web') {
  return async (dispatch: Function) => {
    await dispatch(_appLoaded());

    if (platform === 'web') {
      // applicable only on web app
      if (window.location.href.includes('login-sso/epic-disney'))
        await waitUntilEpicSsoIdTokenIsSet();

      if (window.location.href.includes('login-sso/azure-medtronic'))
        await waitUntilMedtronicSsoIdTokenIsSet();

      // removed the offending code on 9-6-19, however we still need to
      // make sure any passwords that were stored in localStorage are removed
      clearPwLocalStorage();
    }

    try {
      // if we know that there is no idToken there is no reason to query for patient data.
      const idToken = await getIdToken();
      if (!idToken) {
        return dispatch(logout());
      }

      // Query for patient data if they are logged in/have a cognito token stored.
      const { data } = await client.query({ query: PATIENT });
      const { patient } = data;
      if (!patient || !patient.id) {
        return dispatch(logout());
      }

      if (patient.confirmed || hasSsoToken()) {
        // If so update redux to mark user as Logged in.
        // for Epic and Medtronic SSO, we need to update the patient with latest "patient" data
        // to have "Hello <name>" properly filled in on Dashboard
        return dispatch(loginSuccess(patient));
      }

      const cognitoUser = await Auth.currentAuthenticatedUser();
      const cognitoConfirmed =
        cognitoUser.signInUserSession &&
        cognitoUser.signInUserSession.idToken.payload.email_verified;

      if (cognitoConfirmed) {
        confirmPatientInOurDatabase();
        await dispatch(loginSuccess());
      } else {
        await dispatch(loginFailed({ type: 'Unconfirmed' }));
        await dispatch(logout());
        // prevent half logged in, half logged out state
      }
    } catch (error) {
      Honeybadger.notify(error);
      // OK if this fails, as user might not be logged in
      await dispatch(logout());
    }
  };
}

/* ---------- SIGN UP ---------- */

function signUpFailed(error: SignUpError) {
  return {
    type: SIGNUP_FAILED,
    error
  };
}

function signUpSuccess(patient: Patient, confirmationState: string) {
  return {
    type: SIGNUP_SUCCESS,
    patient,
    confirmationState
  };
}

function _signUp(props: SignUpProps) {
  return {
    type: SIGNUP,
    props
  };
}

export function signUp(props: SignUpProps) {
  return async (dispatch: Function) => {
    dispatch(_signUp(props));

    const {
      firstName,
      lastName,
      gender,
      pronoun,
      birthdate,
      phone,
      password,
      labId,
      event,
      address,
      referralSource
    } = props;

    const email = sanitizeUsername(props.email);

    try {
      if (!password) throw new Error('Password must be defined on sign up');

      const scrubbedPhone = phone.replace(/\D/g, '');

      const cognitoUser = await Auth.signUp({
        username: email,
        password: password,
        attributes: {
          email: email,
          birthdate: birthdate,
          phone_number: `+1${scrubbedPhone}`
        }
      });

      const patientData = {
        authComms: true,
        birthdate: moment.tz(birthdate, 'UTC'),
        cognitoId: cognitoUser.userSub,
        confirmed: false,
        email,
        firstName,
        gender,
        pronoun,
        labId,
        lastName,
        phone: scrubbedPhone,
        event,
        address,
        referralSource
      };

      // Create the patient/user on our DB
      const { data } = await client.mutate({
        mutation: CREATE_PATIENT,
        variables: patientData
      });
      const patient = data?.createPatient?.patient;
      if (patient) {
        const confirmationState = cognitoUser.userConfirmed ? 'confirmed' : 'unconfirmed';
        dispatch(signUpSuccess(patientData, confirmationState));
        identifyPatient(patient);
      } else {
        throw new Error('CreatePatientError');
      }
    } catch (error) {
      dispatch(logout());
      if (
        error.code === 'UsernameExistsException' ||
        error.message.includes('Email has already been taken')
      ) {
        dispatch(signUpFailed({ type: 'UserNameExists' }));
      } else {
        dispatch(signUpFailed({ type: 'Unknown' }));
        BugTracker.notify(error, 'SignUpError');
      }
    }
  };
}

/* ---------- SIGN UP WITH GOOGLE ---------- */

function _signUpGoogleUser(props: SignUpProps) {
  return {
    type: SIGNUP_GOOGLE_USER,
    props
  };
}

export function signUpGoogleUser(props: SignUpProps, plaftorm: 'mobile' | 'web' = 'web') {
  return async (dispatch: Function, getState: () => ReduxState) => {
    try {
      dispatch(_signUpGoogleUser(props));

      const {
        patient: {
          patient: { google }
        }
      } = getState();

      if (!google && plaftorm === 'web') {
        dispatch(_googleAuthError({ type: 'UserNotFound' }));
      }

      const {
        firstName,
        lastName,
        gender,
        pronoun,
        birthdate,
        phone,
        labId,
        email: unsanitized,
        event,
        address,
        referralSource
      } = props;

      const email = sanitizeUsername(unsanitized);
      const scrubbedPhone = phone.replace(/\D/g, '');

      const patientData = {
        authComms: true,
        birthdate: moment.tz(birthdate, 'UTC'),
        cognitoId: '',
        confirmed: true,
        email,
        firstName,
        gender,
        pronoun,
        google,
        labId,
        lastName,
        phone: scrubbedPhone,
        event,
        address,
        referralSource
      };

      // Create the patient/user on our DB
      const { data } = await client.mutate({
        mutation: CREATE_PATIENT,
        variables: patientData
      });
      const patient = data?.createPatient?.patient;
      if (patient) {
        dispatch(signUpSuccess(patientData, 'confirmed'));
        identifyPatient(patient);
      } else {
        throw new Error('CreatePatientFromGoogleUserError');
      }
    } catch (error) {
      Honeybadger.notify(error);
      dispatch(logout());
      dispatch(_googleAuthError({ type: 'UserAlreadyExistsAsEmail' }));
    }
  };
}

/* ------- SIGN UP WITH APPLE ------- */

export function _signInAppleUser(token: string, email: string) {
  return {
    type: SIGNIN_APPLE_USER,
    token,
    email
  };
}

export function _setAppleAuthError(error: AppleAuthError | null) {
  return {
    type: SET_APPLE_AUTH_ERROR,
    appleAuthError: error
  };
}

export function signInAppleUser(token: string, email: string, operation: string) {
  return async (dispatch: Function) => {
    let usernameExists;
    try {
      usernameExists = await doesUsernameExists(email);

      if (usernameExists && operation == 'signUp') {
        throw new Error('UserAlreadyExistsAsEmail');
      }

      await dispatch(_setAppleAuthError(null));
      await dispatch(_signInAppleUser(token, email));

      const { data } = await client.query({
        query: PATIENT
      });
      const { patient } = data;

      if (patient && patient.id) {
        // If so update redux to mark user as Logged in
        await dispatch(loginSuccess());
      }
    } catch (error) {
      Honeybadger.notify(error);
      if (operation == 'login') {
        dispatch(_setAppleAuthError({ type: 'UserHasNotSignedUp' }));
        dispatch(logout());
      }

      if (operation == 'signUp') {
        if (usernameExists) {
          dispatch(_setAppleAuthError({ type: 'UserAlreadyExistsAsEmail' }));
          dispatch(logout());
        } else {
          dispatch(_setAppleAuthError({ type: 'UserHasNotSignedUp' }));
        }
      }
    }
  };
}

export function signUpAppleUser(props: SignUpProps & { idtoken?: string }) {
  return async (dispatch: Function, getState: () => ReduxState) => {
    try {
      const {
        firstName,
        lastName,
        gender,
        pronoun,
        birthdate,
        phone,
        labId,
        event,
        address,
        email,
        idtoken,
        referralSource
      } = props;

      const {
        patient: {
          patient: { apple }
        }
      } = getState();

      const scrubbedPhone = phone.replace(/\D/g, '');

      const patientData = {
        authComms: true,
        birthdate: moment.tz(birthdate, 'UTC'),
        confirmed: true,
        email,
        firstName,
        gender,
        pronoun,
        cognitoId: '',
        apple,
        labId,
        lastName,
        phone: scrubbedPhone,
        event,
        address,
        referralSource
      };

      // Create the patient/user on our DB
      const { data } = await client.mutate({
        mutation: CREATE_PATIENT,
        variables: patientData
      });

      const patient = data?.createPatient?.patient;

      if (idtoken) {
        await Cache.setItem('@AppleSSO:idToken', idtoken);
      }

      if (patient) {
        dispatch(signUpSuccess(patientData, 'confirmed'));
        identifyPatient(patient);
      } else {
        throw new Error('CreatePatientFromAppleUserError');
      }
    } catch (error) {
      dispatch(logout());
      throw new Error('CreatePatientFromAppleUserError');
      // dispatch(_googleAuthError({ type: 'UserAlreadyExistsAsEmail' }));
    }
  };
}

/* ---------- CONFIRM USER ---------- */

function confirmUserSuccess() {
  return {
    type: CONFIRM_USER_SUCCESS
  };
}

function confirmUserFailed(error: ConfirmationError) {
  return {
    type: CONFIRM_USER_FAILED,
    error
  };
}

function _confirmUser(code: string, email?: string) {
  return {
    type: CONFIRM_USER,
    code,
    email
  };
}

export function confirmUser(code: string, email?: string) {
  return async (dispatch: Function, getState: () => ReduxState) => {
    dispatch(_confirmUser(code));

    const username = sanitizeUsername(email || getState().patient.patient.email);

    try {
      const confirmSignUp = await Auth.confirmSignUp(username, code);
      if (confirmSignUp === 'SUCCESS') {
        dispatch(confirmUserSuccess());
      }
    } catch (error) {
      if (error.code === 'CodeMismatchException') {
        dispatch(confirmUserFailed({ type: 'InvalidCode' }));
      } else if (error.code === 'ExpiredCodeException') {
        dispatch(confirmUserFailed({ type: 'ExpiredCode' }));
      } else if (error.code === 'AliasExistsException') {
        // adding for error tracking research
        // I think this might be the "User cannot be confirmed. Current status is CONFIRMED" case
        BugTracker.notify(error, 'AliasExistsException');
        dispatch(confirmUserFailed({ type: 'Unknown' }));
      } else {
        BugTracker.notify(error, 'ConfirmUserError');
        dispatch(confirmUserFailed({ type: 'Unknown' }));
      }
      return false;
    }
  };
}

/* ---------- RESEND CONFIRMATION ---------- */

function _resendConfirmationCode() {
  return {
    type: RESEND_CONFIRMATION_CODE
  };
}

function resendConfirmationCodeSuccess() {
  return {
    type: RESEND_CONFIRMATION_CODE_SUCCESS
  };
}

function resendConfirmationCodeFailure(error: string) {
  return {
    type: RESEND_CONFIRMATION_CODE_FAILURE,
    error
  };
}

export function resendConfirmationCode(email: string) {
  return async (dispatch: Function) => {
    try {
      const cleanEmail = sanitizeUsername(email);
      dispatch(_resendConfirmationCode());
      await Auth.resendSignUp(cleanEmail);
      dispatch(resendConfirmationCodeSuccess());
    } catch (error) {
      Honeybadger.notify(error);
      dispatch(resendConfirmationCodeFailure(error));
    }
  };
}

export function updateProfileImage(url?: string) {
  return {
    type: UPDATE_PROFILE_IMAGE,
    url
  };
}

export function profileImageError(error: ProfileImageError) {
  return {
    type: PROFILE_IMAGE_ERROR,
    error
  };
}

export function resetProfileImageError() {
  return {
    type: RESET_PROFILE_IMAGE_ERROR
  };
}

export function updateSubmittedInsuranceCard(submittedInsuranceCard: boolean) {
  return {
    type: UPDATE_SUBMITTED_INSURANCE_CARD,
    submittedInsuranceCard
  };
}

export function insuranceCardError(target: InsuranceCardSide, error: InsuranceCardError) {
  return {
    type: INSURANCE_CARD_ERROR,
    target,
    error
  };
}

export function idError(target: IDSide, error: IDError) {
  return {
    type: ID_ERROR,
    target,
    error
  };
}

export function resetInsuranceCardError(target: InsuranceCardSide) {
  return {
    type: RESET_INSURANCE_CARD_ERROR,
    target
  };
}

export function resetIDError(target: IDSide) {
  return {
    type: RESET_ID_ERROR,
    target
  };
}

export function updateSubmittedID(submittedID: boolean) {
  return {
    type: UPDATE_SUBMITTED_ID,
    submittedID
  };
}

/* ---------- REDUCER ---------- */
const initialState: PatientState = {
  // need this flag to ensure Google users are fully signed out
  userRefreshFailed: false,
  authCheckInProgress: true, // True when app starts
  loading: false,
  isLoggedIn: false,
  confirmationState: null,
  loginError: null,
  signUpError: null,
  confirmationError: null,
  resendConfirmationCode: {
    status: null,
    error: null
  },
  patient: {
    id: '',
    firstName: '',
    lastName: '',
    gender: '',
    birthday: '',
    phone: '',
    email: '',
    submittedInsuranceCard: true,
    confirmed: false,
    submittedID: true
  },
  googleAuthInitialize: false,
  googleAuthError: null,
  appleAuthError: null,
  profileImageError: null,
  insuranceCardErrors: null,
  idErrors: null
};

/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
const handlers = {
  [REFRESH_USER]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      loading: true
    };
  },
  [REFRESH_USER_SUCCESS]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      authCheckInProgress: false,
      loading: false,
      isLoggedIn: true,
      patient: {
        ...state.patient,
        ...action.patient
      }
    };
  },
  [REFRESH_USER_FAILED]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      userRefreshFailed: true,
      authCheckInProgress: false,
      loading: false
    };
  },
  [AUTH_WITH_GOOGLE]: (state: PatientState, action: AnyAction) => ({
    ...state,
    patient: {
      ...state.patient,
      email: action.email,
      firstName: action.firstName,
      lastName: action.lastName,
      google: {
        id: action.id,
        token: action.token
      }
    }
  }),
  [AUTH_WITH_GOOGLE_FAILED]: (state: PatientState, action: AnyAction) => ({
    ...state,
    googleAuthError: action.error,
    loading: false
  }),
  [SIGNIN_APPLE_USER]: (state: PatientState, action: AnyAction) => ({
    ...state,
    loading: true,
    patient: {
      ...state.patient,
      email: action.email,
      apple: {
        token: action.token
      }
    }
  }),
  [SET_APPLE_AUTH_ERROR]: (state: PatientState, action: AnyAction) => ({
    ...state,
    loading: false,
    appleAuthError: action.appleAuthError
  }),
  [CLEAR_GOOGLE_AUTH_ERROR]: (state: PatientState, action: AnyAction) => ({
    ...state,
    googleAuthError: null
  }),
  [LOGIN]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      patient: {
        ...state.patient,
        email: action.username
      },
      loading: true,
      loginError: null
    };
  },
  [LOGIN_SUCCESS]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      isLoggedIn: true,
      authCheckInProgress: false,
      loginError: null,
      patient: action.patient || state.patient,
      loading: false
    };
  },
  [LOGIN_FAILED]: (state: PatientState, action: AnyAction) => {
    let confirmationState: string | null = state.confirmationState;
    if (action.error.type === 'Unconfirmed') {
      confirmationState = 'unconfirmed';
    }

    return {
      ...state,
      authCheckInProgress: false,
      loginError: action.error,
      isLoggedIn: false,
      loading: false,
      confirmationState: confirmationState
    };
  },
  [LOGIN_ERROR_HANDLED]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      loginError: null
    };
  },
  [SIGNUP]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      signUpError: null,
      loading: true
    };
  },
  [SIGNUP_FAILED]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      loading: false,
      signUpError: action.error
    };
  },
  [SIGNUP_SUCCESS]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      loading: false,
      signUpError: null,
      patient: action.patient,
      confirmationState: action.confirmationState,
      isLoggedIn: action.confirmationState === 'confirmed' // auto login Google sign ups that don't require verification
    };
  },
  [CONFIRM_USER]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      confirmationError: null,
      loading: true
    };
  },
  [CONFIRM_USER_SUCCESS]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      confirmationState: 'confirmed',
      loading: false,
      patient: {
        ...state.patient,
        confirmed: true
      }
    };
  },
  [CONFIRM_USER_FAILED]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      confirmationError: action.error,
      loading: false
    };
  },
  [RESEND_CONFIRMATION_CODE]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      resendConfirmationCode: {
        ...state.resendConfirmationCode,
        status: 'loading'
      }
    };
  },
  [RESEND_CONFIRMATION_CODE_SUCCESS]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      resendConfirmationCode: {
        ...state.resendConfirmationCode,
        status: 'success',
        error: null
      }
    };
  },
  [RESEND_CONFIRMATION_CODE_FAILURE]: (state: PatientState, action: AnyAction) => {
    return {
      ...state,
      resendConfirmationCode: {
        ...state.resendConfirmationCode,
        status: 'error',
        error: action.error
      }
    };
  },
  [LOG_OUT]: (state: PatientState, action: AnyAction) => {
    return {
      ...initialState,
      loginUrl: action.loginUrl,
      authCheckInProgress: false
    };
  },
  [UPDATE_PROFILE_IMAGE]: (state: PatientState, action: AnyAction) => ({
    ...state,
    patient: {
      ...state.patient,
      profileImage: action.url
    }
  }),
  [PROFILE_IMAGE_ERROR]: (state: PatientState, action: AnyAction) => ({
    ...state,
    profileImageError: action.error
  }),
  [RESET_PROFILE_IMAGE_ERROR]: (state: PatientState, action: AnyAction) => ({
    ...state,
    profileImageError: null
  }),
  [UPDATE_SUBMITTED_INSURANCE_CARD]: (state: PatientState, action: AnyAction) => ({
    ...state,
    patient: {
      ...state.patient,
      submittedInsuranceCard: action.submittedInsuranceCard
    },
    insuranceCardErrors: action.submittedInsuranceCard ? null : state.insuranceCardErrors
  }),
  [INSURANCE_CARD_ERROR]: (state: PatientState, action: AnyAction) => ({
    ...state,
    patient: {
      ...state.patient,
      submittedInsuranceCard: false
    },
    insuranceCardErrors: {
      ...state.insuranceCardErrors,
      [action.target]: action.error
    }
  }),
  [RESET_INSURANCE_CARD_ERROR]: (state: PatientState, action: AnyAction) => ({
    ...state,
    insuranceCardErrors: {
      ...state.insuranceCardErrors,
      [action.target]: undefined
    }
  }),
  [ID_ERROR]: (state: PatientState, action: AnyAction) => ({
    ...state,
    patient: {
      ...state.patient,
      submittedID: false
    },
    idErrors: {
      ...state.idErrors,
      [action.target]: action.error
    }
  }),
  [RESET_ID_ERROR]: (state: PatientState, action: AnyAction) => ({
    ...state,
    idError: {
      ...state.idErrors,
      [action.target]: undefined
    }
  }),
  [UPDATE_SUBMITTED_ID]: (state: PatientState, action: AnyAction) => ({
    ...state,
    patient: {
      ...state.patient,
      submittedID: action.submittedID
    }
  }),
  [UPDATE_USER_NAME]: (state: PatientState, action: AnyAction) => ({
    ...state,
    patient: {
      ...state.patient,
      firstName: action.firstName,
      lastName: action.lastName
    }
  })
};

export default handleActions(handlers, initialState);
